/**
 * Sign in, the screen that consumes a link, and the screen that sets a password.
 *
 * One route handles all of it. Arriving with `?token=` means somebody tapped a
 * link, and which KIND of link decides what happens: a magic link is exchanged
 * for a session immediately, a setup link asks for a password first.
 *
 * `CLAUDE.md`: every state is designed. There are ten here (asking by
 * password, asking for a link, sending, sent, exchanging, choosing a password,
 * saving it, failed, a sign-out not yet done, finishing it) and none of them is
 * a spinner standing in for a screen.
 *
 * ADR-0029 is why the password half exists. `BUILD_PLAN.md` 4.1 specifies
 * passwordless and "No passwords at all"; the owner overrode that for STAFF.
 * The magic link is still here, unchanged, and it is still the only way in for
 * a guardian, which is where the plan's reasoning applies: a parent who opens
 * this four times a month will not remember a password.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Button, diagnosticFor, errorCopyFor, supportCodeLine } from '@good-season/ui';
import { ApiError, OfflineError, noteSignOut, post, sendSignOut } from '../api/client.js';
import { clearSignedIn, confirmSignedIn } from '../scorebook/owner.js';

/*
 * `sending` and `askingSending` are separate, and the split fixes a real defect
 * rather than being tidiness.
 *
 * There was one `sending`, and the render chain had no branch for it, so it
 * fell through to the DEFAULT, which is the password form. Submitting "Email me
 * a link" therefore swapped the screen to the password form mid-request and
 * back again on failure. The password path was unaffected only because its
 * in-flight state and the default render happen to agree. Graded S2 by the
 * fresh-context pass.
 *
 * Naming the in-flight state per form makes a fall-through impossible rather
 * than merely absent today.
 */
type Stage =
  | 'password'
  | 'sending'
  | 'asking'
  | 'askingSending'
  | 'sent'
  | 'exchanging'
  | 'choosing'
  | 'saving'
  | 'failed'
  | 'unsaved'
  | 'recordKept'
  | 'finishing';

/**
 * A sign-out that neither reached the server nor got saved on the phone (#43,
 * ADR-0090). It lives only in this page's memory, and this screen makes no
 * request on load, so closing the app now would leave the coach signed in for
 * the next person with signal. `useSignOut` sends people here with this state.
 */
const UNSAVED_COPY = {
  title: 'You are not signed out yet.',
  body: 'The sign-out has not reached the server, and this phone may not have saved it to send later. If the app closes now, you may still be signed in here. Keep this screen open until you have signal, then tap Finish signing out.',
  retry:
    'The server has not confirmed it yet. Keep this screen open and try again when you have signal.',
  /** Made on the phone, like SGNOUT, with no server log entry. */
  diagnostic: 'SGNSAV',
};

/**
 * A sign-out that landed while the store would not clear its record of who
 * was signed in (#43, ADR-0090). A full store still reads, so the record goes
 * on naming the coach: this page keeps every book closed, a reload does not,
 * so the copy promises nothing about the scorebook staying closed.
 */
const RECORD_KEPT_COPY = {
  title: 'Signed out, but this phone still has you on record.',
  body: 'The server has signed you out, but this phone could not clear its record of who was signed in. To clear it, restart this phone and free up space on it, then sign in and sign out again with signal. Until then, if the app closes or this page reloads, the next person on this phone may be able to open a saved scorebook while there is no signal.',
  /** Made on the phone, like SGNSAV, with no server log entry. */
  diagnostic: 'SGNREC',
};

/** Two letters naming the surface, per `packages/ui/src/primitives/diagnostic.ts`. */
const SURFACE = 'SI';

const OFFLINE_COPY = {
  title: 'No connection right now.',
  body: 'Your email address is still here. Try again when you have signal.',
};

/**
 * Which kind of link this is, from the URL.
 *
 * `?setup=` rather than a second route, so both kinds of link land on the same
 * screen and the difference is one parameter. A separate route would mean two
 * places that turn a token into a session, and they would drift.
 */
function tokensFrom(params: URLSearchParams): {
  readonly magic: string | null;
  readonly setup: string | null;
  readonly invite: string | null;
} {
  return { magic: params.get('token'), setup: params.get('setup'), invite: params.get('invite') };
}

export function SignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const signOutState = useLocation().state as {
    signOutUnsaved?: unknown;
    signOutRecordKept?: unknown;
  } | null;
  const unsaved = signOutState?.signOutUnsaved === true;
  const recordKept = signOutState?.signOutRecordKept === true;

  const { magic: linkToken, setup: setupToken, invite: inviteToken } = tokensFrom(params);

  const [stage, setStage] = useState<Stage>(() => {
    if (linkToken !== null || inviteToken !== null) return 'exchanging';
    if (setupToken !== null) return 'choosing';
    if (unsaved) return 'unsaved';
    if (recordKept) return 'recordKept';
    // The parent route is drawn first and is the primary action (owner
    // decision, 31 October 2026, issue #183). The default stage is still the
    // password form, drawn below it for coaches and team staff.
    return 'password';
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  /** Came here from "Forgot it?", so the sent screen says what to do next. */
  const [forgot, setForgot] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [retryLink, setRetryLink] = useState(false);
  const [problem, setProblem] = useState<{
    title: string;
    body: string;
    diagnostic?: string;
  } | null>(null);

  /**
   * Move between the forms, clearing the last failure on the way.
   *
   * `problem` is one piece of state and there are three forms, so a failed
   * password attempt left "That email and password do not match" sitting under
   * the magic link form the instant somebody tapped "Email me a link". The
   * sentence is about a password they are no longer being asked for, and it
   * reads as though the new form has already been tried and rejected. Graded S2
   * by the fresh-context pass.
   *
   * Only for switches the PERSON asks for. The two in the catch blocks set a
   * stage and a problem together on purpose, and routing them through here
   * would erase the message they exist to show.
   */
  /**
   * An empty field, answered here rather than by the browser or the server.
   *
   * The inputs carry `aria-required` and not `required`, so nothing stopped an
   * empty form being submitted: the request went out, spent one of ten attempts
   * an hour against that address, and came back as "check the address and the
   * password", which is the wrong answer to "you did not type anything".
   * Caught by the Codex pass and graded P2.
   *
   * Answered in the handler rather than by adding `required`, which is what
   * `PlayerSheet` does. Native validation puts a browser-drawn bubble on screen
   * carrying the browser's words in the browser's language, and `CLAUDE.md` is
   * that every state is designed and no default browser UI stands in for one.
   * The same reasoning as the `alert`/`confirm` ban, one degree softer.
   *
   * Returns true when it has taken over, so each caller reads as a guard.
   */
  function missing(fields: readonly { readonly value: string; readonly label: string }[]): boolean {
    const empty = fields.filter((field) => field.value.trim() === '');
    if (empty.length === 0) return false;

    setProblem({
      title: 'Something is missing.',
      body:
        empty.length === 1
          ? `Fill in your ${empty[0]?.label ?? 'details'} and try again.`
          : `Fill in your ${empty.map((field) => field.label).join(' and ')} and try again.`,
    });
    return true;
  }

  /*
   * Focus follows the form the person asked for. The default screen renders
   * the link button above the form and the link screen renders it below, so
   * the button a person just pressed is unmounted and focus would fall to
   * <body>. Not on first mount, and not when a sign-in fails back to the
   * default screen.
   */
  const previousStage = useRef(stage);
  useEffect(() => {
    const before = previousStage.current;
    previousStage.current = stage;
    if (before === stage) return;
    const leftLinkForm = before === 'asking' || before === 'askingSending';
    if (stage === 'asking' || (stage === 'password' && leftLinkForm)) {
      document.getElementById('email')?.focus();
    }
  }, [stage]);

  function switchTo(next: Stage): void {
    setProblem(null);
    setStage(next);
  }

  useEffect(() => {
    if (linkToken === null) return;

    let cancelled = false;
    void (async () => {
      try {
        await post('/api/auth/callback', { token: linkToken });
        // Who is on this phone now, so their scorebook opens with no signal (#43).
        await confirmSignedIn();
        if (cancelled) return;
        // `/`, which is `Landing` now and decides: a staff invitation waiting
        // on this person first (ADR-0097), then staff to `/team` and a
        // guardian to Home or the wizard. This went to `/team` while `/` was
        // a static ParentHome, and that skipped every one of those decisions:
        // a coach added to a new team signed in and never saw it waiting.
        //
        // Replace, not push. The token is in the URL, and leaving it in history
        // means a back button re-sends a link that is already spent.
        void navigate('/', { replace: true });
      } catch (error) {
        if (cancelled) return;
        setRetryLink(error instanceof OfflineError);
        setProblem(error instanceof OfflineError ? linkOffline(error) : describe(error));
        setStage('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [linkToken, navigate, attempt]);

  /*
   * A guardian's invitation (ADR-0070). Its own endpoint, because the magic
   * link callback deliberately refuses an invite token, and its own landing:
   * the wizard, on the team the link was for, which is what BUILD_PLAN 4.4
   * means by "lands on an onboarding wizard rather than an empty dashboard".
   */
  useEffect(() => {
    if (inviteToken === null) return;

    let cancelled = false;
    void (async () => {
      try {
        const { teamId } = await post<{ teamId: string }>('/api/auth/invite', {
          token: inviteToken,
        });
        await confirmSignedIn();
        if (cancelled) return;
        void navigate(
          { pathname: '/welcome', search: `?${new URLSearchParams({ team: teamId }).toString()}` },
          { replace: true },
        );
      } catch (error) {
        if (cancelled) return;
        setRetryLink(error instanceof OfflineError);
        setProblem(error instanceof OfflineError ? linkOffline(error) : describeInvite(error));
        setStage('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteToken, navigate, attempt]);

  function signInWithPassword(event: React.FormEvent) {
    // preventDefault must run synchronously, before the first await, or the
    // browser navigates and takes the state with it.
    event.preventDefault();
    setProblem(null);

    // A parent who followed "Parents: email yourself a sign-in link" and typed
    // their address here would otherwise be told only to fill in a password.
    if (password.trim() === '' && /^\S+@\S+$/.test(email.trim())) {
      setProblem({
        title: 'Something is missing.',
        body: 'Fill in your password and try again. Parents do not need a password: use Email me a link above.',
      });
      return;
    }

    if (
      missing([
        { value: email, label: 'email' },
        { value: password, label: 'password' },
      ])
    ) {
      return;
    }

    setStage('sending');

    void (async () => {
      try {
        await post('/api/auth/password', { email: email.trim(), password });
        await confirmSignedIn();
        // `/`, for the reason the magic link gives above (ADR-0097).
        void navigate('/', { replace: true });
      } catch (error) {
        /*
         * One message for every refusal, because the server gives one answer.
         *
         * Wrong password, no account, and an account that may not use a
         * password all return the same code, deliberately: anything else tells
         * an unauthenticated caller which addresses have accounts here, and on
         * a youth sports platform that is which families are on which team. The
         * screen must not undo that by guessing.
         */
        setProblem(describeSignIn(error));
        setStage('password');
        // The password is cleared and the address is not. Retyping an address
        // after a mistyped password is the app being unhelpful.
        setPassword('');
      }
    })();
  }

  function requestLink(event: React.FormEvent) {
    event.preventDefault();
    setProblem(null);

    if (missing([{ value: email, label: 'email' }])) return;

    setStage('askingSending');

    void (async () => {
      try {
        await post('/api/auth/request-link', { email: email.trim() });
        setStage('sent');
      } catch (error) {
        setProblem(describe(error));
        setStage('asking');
      }
    })();
  }

  function choosePassword(event: React.FormEvent) {
    event.preventDefault();
    setProblem(null);

    if (
      missing([
        { value: password, label: 'password' },
        { value: confirmation, label: 'second password' },
      ])
    ) {
      return;
    }

    if (password !== confirmation) {
      // Checked here rather than by the server, because the server never sees
      // the second box. It exists so a typo does not become a password nobody
      // knows, which with no self-service reset means going back to a coach.
      setProblem({
        title: 'Those do not match.',
        body: 'The two passwords are different. Check them and try again.',
      });
      return;
    }

    setStage('saving');
    void (async () => {
      try {
        await post('/api/auth/password/set', { token: setupToken, password });
        await confirmSignedIn();
        void navigate('/team', { replace: true });
      } catch (error) {
        setProblem(describeSetup(error));
        setStage('choosing');
      }
    })();
  }

  function finishSigningOut(): void {
    setProblem(null);
    setStage('finishing');
    void (async () => {
      // Noted again, so a reload that kept this notice but lost the page's copy still sends one.
      noteSignOut();
      try {
        await sendSignOut();
        // The store refused the first write, so it may still name the coach.
        const cleared = await clearSignedIn();
        // Drops the notice from history, so a reload shows the plain sign-in.
        void navigate('/signin', {
          replace: true,
          state: cleared ? null : { signOutRecordKept: true },
        });
        setStage(cleared ? 'password' : 'recordKept');
      } catch {
        setProblem({ title: UNSAVED_COPY.title, body: UNSAVED_COPY.retry });
        setStage('unsaved');
      }
    })();
  }

  if (stage === 'unsaved' || stage === 'finishing') {
    return (
      <Frame
        title={UNSAVED_COPY.title}
        body={UNSAVED_COPY.body}
        diagnostic={UNSAVED_COPY.diagnostic}
      >
        {problem === null ? null : (
          <p className="signin__error" role="alert">
            {problem.body}
          </p>
        )}
        <div className="signin__actions">
          <Button onClick={finishSigningOut} disabled={stage === 'finishing'}>
            {stage === 'finishing' ? 'Signing out' : 'Finish signing out'}
          </Button>
        </div>
      </Frame>
    );
  }

  if (stage === 'recordKept') {
    return (
      <Frame
        title={RECORD_KEPT_COPY.title}
        body={RECORD_KEPT_COPY.body}
        diagnostic={RECORD_KEPT_COPY.diagnostic}
      >
        <div className="signin__actions">
          <Button onClick={() => switchTo('password')}>Sign in</Button>
        </div>
      </Frame>
    );
  }

  if (stage === 'exchanging') {
    return (
      <Frame title="Signing you in" body="One moment.">
        <Link className="gs-button gs-button--ghost signin__actions" to="/">
          Back to home
        </Link>
      </Frame>
    );
  }

  if (stage === 'failed') {
    return (
      <Frame
        title={problem?.title ?? 'That link did not work.'}
        body={problem?.body ?? 'Request a new one and it will work.'}
        diagnostic={problem?.diagnostic}
      >
        <div className="signin__actions">
          {retryLink ? (
            <Button
              onClick={() => {
                switchTo('exchanging');
                setRetryLink(false);
                setAttempt((previous) => previous + 1);
              }}
            >
              Try again
            </Button>
          ) : null}
          <Button onClick={() => switchTo('asking')}>Send a new link</Button>
          <Link className="gs-button gs-button--ghost" to="/">
            Back to home
          </Link>
        </div>
      </Frame>
    );
  }

  if (stage === 'sent') {
    return (
      <Frame
        title="Check your email."
        // Deliberately does not confirm the address is known. The API answers
        // identically either way, and a screen that said "we found you" would
        // undo that in one sentence.
        body={`If ${email.trim()} is on a team, a sign-in link is on its way. It works once and expires in 30 minutes.${
          forgot
            ? ' Once you are in, choose Change password at the top of the screen within 15 minutes to set a new one.'
            : ''
        }`}
      >
        <div className="signin__actions">
          <Button tone="ghost" onClick={() => switchTo('asking')}>
            Use a different address
          </Button>
        </div>
      </Frame>
    );
  }

  if (stage === 'choosing' || stage === 'saving') {
    return (
      <Frame
        title="Choose a password."
        body="This is how you will sign in from now on. Pick something long that you will remember."
      >
        <form
          onSubmit={choosePassword}
          className="signin__form"
          /*
          `noValidate`, the same as `AddPersonSheet`, and it is a statement
          rather than a switch being turned off.

          These inputs carry `aria-required` and not `required`, so the browser
          has nothing to enforce today and this changes nothing today. It is
          here so that adding `required` later does not silently start drawing a
          browser-styled bubble in the browser's words over a screen where every
          other message is designed and written. The empty-field answer lives in
          `missing()`, which also skips a request that would spend one of ten
          attempts an hour.
        */
          noValidate
        >
          <label htmlFor="new-password" className="signin__label">
            New password
          </label>
          <input
            id="new-password"
            className="gs-input"
            type="password"
            // `new-password`, so a browser offers to generate one and does not
            // fill in the password for some other site.
            autoComplete="new-password"
            aria-describedby="password-hint"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-required="true"
          />
          <p id="password-hint" className="signin__hint">
            At least 12 characters. A short phrase you can type is better than something short and
            cryptic.
          </p>

          <label htmlFor="confirm-password" className="signin__label">
            Type it again
          </label>
          <input
            id="confirm-password"
            className="gs-input"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            aria-required="true"
          />

          {problem === null ? null : (
            <p className="signin__error" role="alert">
              {problem.body}
              {problem.diagnostic === undefined ? null : ` ${supportCodeLine(problem.diagnostic)}`}
            </p>
          )}

          <Button type="submit" disabled={stage === 'saving'}>
            {stage === 'saving' ? 'Saving' : 'Save and sign in'}
          </Button>
        </form>
      </Frame>
    );
  }

  if (stage === 'asking' || stage === 'askingSending') {
    return (
      <Frame
        title="Email me a link"
        body="No password needed. The link works once and expires in 30 minutes."
      >
        <form onSubmit={requestLink} className="signin__form" noValidate>
          <label htmlFor="email" className="signin__label">
            Your email
          </label>
          <input
            id="email"
            className="gs-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-required="true"
          />
          {problem === null ? null : (
            <p className="signin__error" role="alert">
              {problem.body}
              {problem.diagnostic === undefined ? null : ` ${supportCodeLine(problem.diagnostic)}`}
            </p>
          )}
          <Button type="submit" disabled={stage === 'askingSending'}>
            {stage === 'askingSending' ? 'Sending' : 'Email me a link'}
          </Button>
        </form>
        <div className="signin__actions">
          <Button
            tone="ghost"
            onClick={() => switchTo('password')}
            disabled={stage === 'askingSending'}
          >
            I have a password
          </Button>
        </div>
      </Frame>
    );
  }

  return (
    <Frame title="Sign in" body="Parents: email yourself a sign-in link. No password needed.">
      <div className="signin__actions">
        <h2 className="signin__section">Parents</h2>
        {/*
          The guardian's route, named for what it is rather than for what it is
          not. A parent looking at this screen has no password and never will,
          so "Email me a link" has to read as the normal thing it is, not as a
          fallback for people who forgot something. It sits above the password
          form and is the one primary action on this screen (owner approved,
          issue #183).
        */}
        {/*
          Disabled while a request is in flight.

          Without it, tapping submit and then switching let the slower response
          land on the other form: "Check your email." replacing a password form
          mid-typing, or an error wiping what was typed. Same class as the
          defect this file's own header comment claims to have fixed, one form
          over. Graded S2 by the fresh-context pass.
        */}
        <Button
          onClick={() => {
            setForgot(false);
            switchTo('asking');
          }}
          disabled={stage === 'sending'}
        >
          Email me a link
        </Button>
      </div>

      <h2 id="signin-staff-heading" className="signin__section">
        Coaches and team staff: sign in with your password
      </h2>
      <form
        onSubmit={signInWithPassword}
        className="signin__form"
        aria-labelledby="signin-staff-heading"
        noValidate
      >
        <label htmlFor="email" className="signin__label">
          Your email
        </label>
        <input
          id="email"
          className="gs-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-required="true"
        />

        <label htmlFor="password" className="signin__label">
          Password
        </label>
        <input
          id="password"
          className="gs-input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-required="true"
        />
        {/*
          Issue #103, ADR-0101. Recovery is the magic link a guardian already
          uses, not a reset token: same request, same neutral answer, same rate
          limit. Once signed in, Change password sets a new one.
        */}
        <Button
          tone="quiet"
          onClick={() => {
            setForgot(true);
            switchTo('asking');
          }}
          disabled={stage === 'sending'}
        >
          Forgot it? Email yourself a sign-in link.
        </Button>

        {problem === null ? null : (
          <p className="signin__error" role="alert">
            {problem.body}
            {problem.diagnostic === undefined ? null : ` ${supportCodeLine(problem.diagnostic)}`}
          </p>
        )}

        {/* Ghost, so the emailed-link button above stays the only primary action. */}
        <Button tone="ghost" type="submit" disabled={stage === 'sending'}>
          {stage === 'sending' ? 'Signing in' : 'Sign in'}
        </Button>
      </form>
    </Frame>
  );
}

/**
 * A refused password sign-in, in one sentence that does not say why.
 *
 * The server answers identically for a wrong password, an unknown address, and
 * an account that may not use a password. This must not be more helpful than
 * that, because being more helpful means confirming which addresses have
 * accounts on a platform holding children's data.
 *
 * It does say what to do next, which `CLAUDE.md` requires: the link is the way
 * in for anybody who does not have a password, and it is one tap away.
 */
/** The offline copy, with a code like every other failure (#106). */
function offline(error: unknown): { title: string; body: string; diagnostic: string } {
  return { ...OFFLINE_COPY, diagnostic: diagnosticFor(error, SURFACE) };
}

function describeSignIn(error: unknown): { title: string; body: string; diagnostic?: string } {
  if (error instanceof OfflineError) return offline(error);
  if (error instanceof ApiError && error.code === 'AUTH_LINK_INVALID') {
    return {
      title: 'That did not work.',
      body: 'Check the address and the password. If you do not have a password, email yourself a link instead.',
      diagnostic: error.diagnostic,
    };
  }
  return describe(error);
}

/**
 * A refused invitation. Unlike a setup link, the coach CAN send a new one
 * (the Resend control on the player's card), so the copy says so; and an
 * account that already exists can still ask for a plain sign-in link below.
 */
function describeInvite(error: unknown): { title: string; body: string; diagnostic?: string } {
  if (error instanceof OfflineError) return offline(error);
  if (error instanceof ApiError && error.code === 'AUTH_LINK_INVALID') {
    return {
      title: 'That invitation did not work.',
      body: 'Invitations last 14 days and work once. Ask your coach to send a new one, or email yourself a sign-in link if you have used Good Season before.',
      diagnostic: error.diagnostic,
    };
  }
  return describe(error);
}

/** A refused setup link. The two reasons need different answers. */
function describeSetup(error: unknown): { title: string; body: string; diagnostic?: string } {
  if (error instanceof OfflineError) return offline(error);

  if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
    /*
     * The password itself, and the fix is the person's.
     *
     * Distinct from a bad link, which sends them back to their coach. Telling
     * somebody "that link has expired" when they typed eleven characters is how
     * they end up asking for a new link they do not need.
     */
    return {
      title: 'That password is too easy to guess.',
      body: 'Use at least 12 characters. A short phrase works well.',
      diagnostic: error.diagnostic,
    };
  }

  if (error instanceof ApiError && error.code === 'AUTH_LINK_INVALID') {
    /*
     * "Expired" is one of three reasons, and the other two the coach cannot fix.
     *
     * `AUTH_LINK_INVALID` comes back from `/password/set` for an expired or
     * spent link, and ALSO for `membership-gone` and `joined-elsewhere-since`.
     * In that last case the account has picked up a membership somewhere else
     * since the link was minted, `canIssueSetup` then refuses their coach a
     * replacement, and the People screen does not draw the button.
     *
     * The FIRST attempt at this changed only the title and left "Ask your coach
     * for a new one" in place, under a comment presenting the change as the
     * fix. It was not: the two precedents it cited had both removed the
     * person-pointer, and a test in this file was pinning the sentence.
     * Graded S2 by the fresh-context pass, twice, on the same three words.
     *
     * The coach is gone. Not because asking is always wrong, it is exactly
     * right for an expired link, but because the client cannot tell which of
     * the three it is and the server will not say: distinguishing them would
     * tell an unauthenticated caller whether an address holds a membership
     * elsewhere. Copy that cannot know which case it is in has to be true in
     * all of them, and "ask the person who sent it" is not.
     *
     * The title no longer asserts "expired" for the same reason.
     */
    return {
      title: 'That link did not work.',
      body: 'Setup links last three days and work once. Contact support and quote your email address, and somebody will sort out a new one.',
      diagnostic: error.diagnostic,
    };
  }

  return describe(error);
}

function linkOffline(error: OfflineError): { title: string; body: string; diagnostic: string } {
  return {
    title: 'No connection right now.',
    body: "Couldn't finish signing you in in time. Try again. If the link was already used, send a new one.",
    diagnostic: diagnosticFor(error, SURFACE),
  };
}

/** Turn whatever was thrown into copy, never into a status code. */
function describe(error: unknown): { title: string; body: string; diagnostic?: string } {
  if (error instanceof OfflineError) return offline(error);
  if (error instanceof ApiError) {
    const copy = errorCopyFor(error.code);
    return { title: copy.title, body: copy.body, diagnostic: error.diagnostic };
  }
  const copy = errorCopyFor();
  return { title: copy.title, body: copy.body, diagnostic: diagnosticFor(error, SURFACE) };
}

interface FrameProps {
  readonly title: string;
  readonly body: string;
  readonly diagnostic?: string | undefined;
  readonly children?: React.ReactNode;
}

export function Frame({ title, body, diagnostic, children }: FrameProps) {
  return (
    <main className="signin">
      <div className="signin__card">
        <h1 className="signin__title">{title}</h1>
        <p className="signin__body">{body}</p>
        {children}
        {diagnostic === undefined ? null : (
          <p className="signin__code">{supportCodeLine(diagnostic)}</p>
        )}
      </div>
    </main>
  );
}
